import { isPlatformBrowser } from '@angular/common';
import { Component, OnDestroy, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { Router } from '@angular/router';
import { LocaleService } from '@next/core-lib/i18n';
import { SiteActions, SiteState } from '@next/core-lib/site';
import { UserSelectors } from '@next/core-lib/user';
import { select, Store } from '@ngrx/store';
import { Subscription, combineLatest } from 'rxjs';
import { debounceTime, filter, map, take } from 'rxjs/operators';
import { getYear, selectCustomerProductInventoryData, selectOrderPlanLoaded, selectPreferredDeliveryPeriods } from 'src/app/store/selectors';

import { getOrderPlan } from '../../../store/actions';
import { OrderplanCropAidDisplayData } from '../../models';

@Component({
  selector: 'app-customer-product-inventory-print',
  templateUrl: './customer-product-inventory-print.component.html',
  styleUrls: ['./customer-product-inventory-print.component.scss'],
  standalone: false,
})
export class CustomerProductInventoryPrintComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly platform = inject(PLATFORM_ID);
  private readonly locale = inject(LocaleService);
  protected readonly router = inject(Router);

  subscription = new Subscription();
  customer$ = this.store.pipe(select(UserSelectors.selectCustomer));
  orderPlanByPeriodLoaded$ = this.store.pipe(select(selectOrderPlanLoaded));
  cropAids$ = this.store.pipe(
    select(selectCustomerProductInventoryData),
    map((cropAidData) =>
      cropAidData.toSorted((a, b) => {
        if (a.cropAid?.name && b.cropAid?.name) {
          if (a.cropAid.name < b.cropAid.name) return -1;
          if (a.cropAid.name > b.cropAid.name) return 1;
        }
        return 0;
      }),
    ),
  );
  hasCropAids = true;
  displayedColumns = ['name', 'requisite', 'inventory', 'ordered', 'result'];
  year$ = this.store.pipe(
    select(selectPreferredDeliveryPeriods),
    filter((data) => !!data),
    map((data) => data.year),
  );

  ngOnInit() {
    this.subscription.add(
      combineLatest([this.customer$, this.year$]).subscribe(([customer, year]) => {
        const pageTitle = `${customer?.name ?? ''} ${this.locale.translate('productInventoryOverview')} ${year ?? ''}`;
        this.store.dispatch(SiteActions.updatePageTitle({ pageTitle }));
      }),
    );
    this.subscription.add(
      this.store.select(getYear).subscribe((_) => {
        this.store.dispatch(getOrderPlan());
      }),
    );

    this.subscription.add(
      combineLatest([this.orderPlanByPeriodLoaded$, this.cropAids$])
        .pipe(debounceTime(200))
        .subscribe(([orderPlanByPeriodLoaded, cropAids]) => {
          if (orderPlanByPeriodLoaded) {
            this.hasCropAids = !!(cropAids && cropAids.length);
          }
        }),
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  getTotalUsedAmount(cropAidData: OrderplanCropAidDisplayData): number {
    return cropAidData.customerProductInventoryAmount + cropAidData.orderLinesTotalAmount;
  }

  getShortageAmount(cropAidData: OrderplanCropAidDisplayData) {
    const totalUsedAmount = this.getTotalUsedAmount(cropAidData);
    const shortage = cropAidData.requisiteAmount - totalUsedAmount;

    return shortage >= 0 ? shortage : 0;
  }

  getExcessAmount(cropAidData: OrderplanCropAidDisplayData) {
    const totalUsedAmount = this.getTotalUsedAmount(cropAidData);
    const excessAmount = totalUsedAmount - cropAidData.requisiteAmount;

    return excessAmount >= 0 ? excessAmount : 0;
  }

  print() {
    combineLatest([this.cropAids$, this.customer$])
      .pipe(take(1))
      .subscribe(([cropAids, customer]) => {
        if (isPlatformBrowser(this.platform) && cropAids && cropAids.length && customer) {
          setTimeout(window.print, 1500);
        }
      });
  }

  back() {
    this.router.navigate(['/']);
  }
}
